import { IconChevronLeft, IconChevronRight, IconFilter } from '@tabler/icons-react';
import { NSButton, NSInput } from 'bricks';
import NSIcon from 'bricks/NSIcon/NSIcon';
import { useCallback, useMemo, useState } from 'react';
import { Dropdown, DropdownMenu, DropdownToggle, DropdownItem } from 'reactstrap';
import { EMPTY_DEAL_FILTERS, DATE_OPERATOR_OPTIONS, NUMBER_OPERATOR_OPTIONS } from './constants';
import {
    DateRangeOperator,
    IDealFilterOptions,
    IDealFilters,
    NumberRangeOperator,
    IDateRangePanel,
    IFilterPanel,
    INumberRangePanel,
    IPickListPanel,
} from '../types';
import { FILTER_PANELS } from './constants';
import { NSMultiSelect, NSThemeProvider, NSSelectOption, NSSelect } from '@northspyre-inc/ui';
import { getFilterKey } from '../helpers/dealFilters';
import './FiltersPopover.scss';
import '@northspyre-inc/ui/styles.css';

export interface IFiltersPopoverProps {
    appliedFilters: IDealFilters;
    filterOptions: IDealFilterOptions;
    onApply: (filters: IDealFilters) => void;
}

const FiltersPopover: React.FC<IFiltersPopoverProps> = ({ appliedFilters, filterOptions, onApply }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [activePanel, setActivePanel] = useState<IFilterPanel | null>(null);
    const [draftFilters, setDraftFilters] = useState<IDealFilters>({ ...appliedFilters });
    const [numberOperator, setNumberOperator] = useState<NumberRangeOperator>('>');
    const [dateOperator, setDateOperator] = useState<DateRangeOperator>('After');

    const toggle = useCallback(() => {
        setIsOpen(prev => !prev);
        setActivePanel(null);
    }, []);

    const openPanel = useCallback(
        (panel: IFilterPanel) => {
            setDraftFilters({ ...appliedFilters });

            if (panel.type === 'date-range') {
                const { beforeKey } = panel as IDateRangePanel;
                setDateOperator(appliedFilters[beforeKey] ? 'Before' : 'After');
            } else if (panel.type === 'number-range') {
                const { maxKey } = panel as INumberRangePanel;
                setNumberOperator(appliedFilters[maxKey] ? '<' : '>');
            }

            setActivePanel(panel);
        },
        [appliedFilters],
    );

    const handleReset = useCallback(() => {
        if (!activePanel) return;

        switch (activePanel.type) {
            case 'pick-list': {
                const { filterKey } = activePanel as IPickListPanel;
                setDraftFilters(prev => ({
                    ...prev,
                    [filterKey]: EMPTY_DEAL_FILTERS[filterKey],
                }));
                break;
            }
            case 'date-range': {
                const { afterKey, beforeKey } = activePanel as IDateRangePanel;
                setDraftFilters(prev => ({
                    ...prev,
                    [afterKey]: undefined,
                    [beforeKey]: undefined,
                }));
                break;
            }
            case 'number-range': {
                const { minKey, maxKey } = activePanel as INumberRangePanel;
                setDraftFilters(prev => ({
                    ...prev,
                    [minKey]: undefined,
                    [maxKey]: undefined,
                }));
                break;
            }
            default:
                // No-op: all panel types are handled above
                break;
        }
    }, [activePanel]);

    const handleCancel = useCallback(() => {
        setDraftFilters({ ...appliedFilters });
        setActivePanel(null);
    }, [appliedFilters]);

    const handleApply = useCallback(() => {
        onApply(draftFilters);
        setActivePanel(null);
        setIsOpen(false);
    }, [draftFilters, onApply]);

    const handleFilterChange = useCallback((key: string, value: (string | number)[] | string | undefined) => {
        setDraftFilters(prev => ({
            ...prev,
            [key]: value || undefined,
        }));
    }, []);

    const getPickListOptions = useCallback(
        (panel: IPickListPanel): NSSelectOption[] => {
            const options = filterOptions[panel.optionsKey] || [];
            return options.map(opt => ({ label: opt.label, value: opt.value }));
        },
        [filterOptions],
    );

    const getPickListValue = useCallback(
        (panel: IPickListPanel): NSSelectOption[] => {
            const selectedValues = draftFilters[panel.filterKey] || [];
            const options = filterOptions[panel.optionsKey] || [];
            return options
                .filter(opt => selectedValues.includes(opt.value))
                .map(opt => ({ label: opt.label, value: opt.value }));
        },
        [draftFilters, filterOptions],
    );

    const renderPickListEditor = useMemo(() => {
        if (!activePanel || activePanel.type !== 'pick-list') return null;
        const panel = activePanel as IPickListPanel;

        return (
            <NSMultiSelect
                placeholder='Select all'
                options={getPickListOptions(panel)}
                value={getPickListValue(panel).map(opt => opt.value)}
                onChange={options => handleFilterChange(panel.filterKey, options)}
                menuMaxHeight="50vh"
                controlWidth="100%"
                isSearchable
            />
        );
    }, [activePanel, getPickListOptions, getPickListValue, handleFilterChange]);

    const renderRangeEditor = useCallback(
        <T extends DateRangeOperator | NumberRangeOperator>(
            panelType: 'date-range' | 'number-range',
            operator: T,
            setOperator: (op: T) => void,
            operatorOptions: NSSelectOption[],
            panel: IDateRangePanel | INumberRangePanel,
            inputType: 'date' | 'number',
        ) => {
            const filterKey = getFilterKey(operator, panelType, panel);
            const selectedOperator = operatorOptions.find(opt => opt.value === operator);

            const handleOperatorChange = (newOperator: T) => {
                setDraftFilters(prev => ({
                    ...prev,
                    [filterKey]: appliedFilters[filterKey],
                }));
                setOperator(newOperator);
            };

            return (
                <div className="d-flex justify-content-between align-items-center">
                    <NSSelect
                        options={operatorOptions}
                        value={selectedOperator?.value ?? null}
                        onChange={value => handleOperatorChange(value as T)}
                        isClearable={false}
                        className='mr-2'
                    />
                    <NSInput
                        type={inputType}
                        name={filterKey}
                        inputWidth={inputType === 'number' ? '200px' : '180px'}
                        placeholder={inputType === 'number' ? '0' : undefined}
                        value={draftFilters[filterKey] || ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            handleFilterChange(filterKey, e.target.value)
                        }
                    />
                </div>
            );
        },
        [draftFilters, handleFilterChange, appliedFilters],
    );

    const renderDateRangeEditor = useMemo(() => {
        if (!activePanel || activePanel.type !== 'date-range') return null;
        const panel = activePanel as IDateRangePanel;

        return renderRangeEditor(
            'date-range',
            dateOperator,
            setDateOperator,
            DATE_OPERATOR_OPTIONS,
            panel,
            'date',
        );
    }, [activePanel, dateOperator, renderRangeEditor]);

    const renderNumberRangeEditor = useMemo(() => {
        if (!activePanel || activePanel.type !== 'number-range') return null;
        const panel = activePanel as INumberRangePanel;

        return renderRangeEditor(
            'number-range',
            numberOperator,
            setNumberOperator,
            NUMBER_OPERATOR_OPTIONS,
            panel,
            'number',
        );
    }, [activePanel, numberOperator, renderRangeEditor]);

    const renderPanelEditor = useMemo(() => {
        if (!activePanel) return null;

        switch (activePanel.type) {
            case 'pick-list':
                return renderPickListEditor;
            case 'date-range':
                return renderDateRangeEditor;
            case 'number-range':
                return renderNumberRangeEditor;
            default:
                return null;
        }
    }, [activePanel, renderPickListEditor, renderDateRangeEditor, renderNumberRangeEditor]);

    return (
        <Dropdown isOpen={isOpen} toggle={toggle}>
            <DropdownToggle tag="div">
                <NSButton
                    icon={IconFilter}
                    text="Filters"
                    color="secondary"
                    className="text-nowrap mr-2"
                />
            </DropdownToggle>
            <NSThemeProvider mode="dark" className="tw-h-full">
                <DropdownMenu className="FiltersPopover__dropdown-menu" right>
                    {!activePanel ? (
                        <>
                            <div className="p-2">FILTER BY</div>
                            {FILTER_PANELS.map(panel => (
                                <DropdownItem
                                    key={panel.id}
                                    className="d-flex justify-content-between align-items-center"
                                    onClick={() => openPanel(panel)}
                                    toggle={false}
                                >
                                    <span>{panel.label}</span>
                                    <NSIcon icon={IconChevronRight} />
                                </DropdownItem>
                            ))}
                        </>
                    ) : (
                        <>
                            <div className="d-flex justify-content-between align-items-center ml-1 mr-2 mt-2">
                                <NSButton
                                    callback={handleCancel}
                                    className="text-dark"
                                >
                                    <NSIcon icon={IconChevronLeft} size="sm" />
                                    Back
                                </NSButton>
                                <NSButton
                                    className="text-primary"
                                    callback={handleReset}
                                >
                                    RESET
                                </NSButton>
                            </div>
                            <span className="ml-3 mt-1 font-weight-bold">{activePanel.label.toUpperCase()}</span>
                            <div className='mx-3 mt-2'>{renderPanelEditor}</div>
                            <div className="d-flex justify-content-end m-3">
                                <NSButton text="Cancel" callback={handleCancel} />
                                <NSButton text="Apply" color="primary" callback={handleApply} />
                            </div>
                        </>
                    )}
                </DropdownMenu>
            </NSThemeProvider>
        </Dropdown>
    );
};

export default FiltersPopover;
